import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { PricingProvider } from './contexts/PricingContext';
import Navbar from './components/Navbar';
import CartModal from './components/CartModal';
import ScrollToTop from './components/ScrollToTop';
import PurchasePricingBar, { matchesPurchaseFlow } from './components/PurchasePricingBar';
import AnalyticsTracker from './components/AnalyticsTracker';
import { trackEvent } from './services/analyticsService';
import { usePricing } from './contexts/PricingContext';
import {
  findMergeablePackIndex,
  isPackItem,
  mergePackFiles,
  newPackCartId,
} from './utils/cartPacks';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const SchoolsPage = React.lazy(() => import('./pages/SchoolsPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));
const AboutMePage = React.lazy(() => import('./pages/AboutMePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const SchoolDaysPage = React.lazy(() => import('./pages/SchoolDaysPage'));
const DayPhotosPage = React.lazy(() => import('./pages/DayPhotosPage'));
const LegalNoticePage = React.lazy(() => import('./pages/LegalNoticePage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage = React.lazy(() => import('./pages/TermsConditionsPage'));
const CheckoutSuccessPage = React.lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = React.lazy(() => import('./pages/CheckoutCancelPage'));
const AccountLoginPage = React.lazy(() => import('./pages/account/AccountLoginPage'));
const AccountGalleryPage = React.lazy(() => import('./pages/account/AccountGalleryPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/account/VerifyEmailPage'));
const AccountResetPasswordPage = React.lazy(() => import('./pages/account/AccountResetPasswordPage'));
const OAuthCallbackPage = React.lazy(() => import('./pages/account/OAuthCallbackPage'));
const PhotographerRegisterPage = React.lazy(() => import('./pages/PhotographerRegisterPage'));

const PENDING_CART_STORAGE_KEY = 'surf_pending_checkout_cart';

const RouteFallback: React.FC = () => (
  <div className="route-fallback" aria-busy="true" aria-label="Cargando página">
    <div className="route-fallback-spinner" />
  </div>
);

interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  /** Número de fotos cuando es un pack (Pack Completo) */
  photoCount?: number;
  /** Para packs: lista de nombres de archivo para descarga */
  files?: string[];
  school?: string;
  date?: string;
}

/** Recalcula precios de packs: primer pack al precio configurado, siguientes al adicional. */
function getDisplayItems(
  items: CartItem[],
  packFirst: number,
  packAdditional: number,
): CartItem[] {
  let packIndex = 0;
  return items.map((item) => {
    if (!item.id.startsWith('pack_')) return item;
    packIndex += 1;
    const price = packIndex === 1 ? packFirst : packAdditional;
    return { ...item, price };
  });
}

const AppMain: React.FC = () => {
  const location = useLocation();
  const showPurchasePricingBar = matchesPurchaseFlow(location.pathname);
  const { packFirst, packAdditional } = usePricing();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  const addToCart = (item: CartItem) => {
    setCartItems((prevItems) => {
      if (isPackItem(item)) {
        const mergeIdx = findMergeablePackIndex(prevItems, item);

        if (mergeIdx >= 0) {
          const existing = prevItems[mergeIdx];
          const files = mergePackFiles(existing.files, item.files);
          const photoCount = files?.length ?? item.photoCount ?? existing.photoCount;
          const merged: CartItem = {
            ...existing,
            ...item,
            id: existing.id,
            files,
            photoCount,
          };
          const next = prevItems.filter((candidate, idx) => {
            if (idx === mergeIdx) return false;
            if (
              !isPackItem(candidate) &&
              item.school &&
              candidate.school === item.school &&
              (!item.date || !candidate.date || candidate.date === item.date)
            ) {
              return false;
            }
            return true;
          });
          return [...next, merged];
        }

        const packToAdd: CartItem = {
          ...item,
          id: newPackCartId(item.school, item.date),
        };
        const next = prevItems.filter((candidate) => {
          if (
            !isPackItem(candidate) &&
            item.school &&
            candidate.school === item.school &&
            (!item.date || !candidate.date || candidate.date === item.date)
          ) {
            return false;
          }
          return true;
        });
        return [...next, packToAdd];
      }

      const coveredByPack = prevItems.some(
        (candidate) =>
          isPackItem(candidate) &&
          Boolean(candidate.school) &&
          candidate.school === item.school &&
          (!candidate.date || !item.date || candidate.date === item.date),
      );
      if (coveredByPack) return prevItems;
      if (prevItems.some((candidate) => candidate.id === item.id)) return prevItems;
      return [...prevItems, { ...item }];
    });
    trackEvent('add_to_cart', {
      school: item.school,
      folder: item.school,
      session_date: item.date,
      metadata: { item_id: item.id, price: item.price },
    });
    setShowCart(true);
  };

  const removeFromCart = (id: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const displayItems = getDisplayItems(cartItems, packFirst, packAdditional);
  const getTotalPrice = () => displayItems.reduce((total, item) => total + item.price, 0);

  // Al iniciar el pago: guardamos el carrito por si el cliente cancela (para no
  // perder su selección) y lo vaciamos de inmediato para que, si vuelve atrás
  // desde Stripe, no vea el mismo carrito lleno y termine pagando dos veces.
  const handleCheckoutStart = () => {
    try {
      sessionStorage.setItem(PENDING_CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // sessionStorage no disponible (modo privado, etc.): no es crítico.
    }
    setCartItems([]);
    setShowCart(false);
  };

  const restorePendingCart = () => {
    try {
      const raw = sessionStorage.getItem(PENDING_CART_STORAGE_KEY);
      sessionStorage.removeItem(PENDING_CART_STORAGE_KEY);
      if (raw) {
        const restored = JSON.parse(raw) as CartItem[];
        if (Array.isArray(restored) && restored.length > 0) {
          setCartItems(restored);
        }
      }
    } catch {
      // Ignorar carritos guardados corruptos.
    }
  };

  const clearPendingCart = () => {
    try {
      sessionStorage.removeItem(PENDING_CART_STORAGE_KEY);
    } catch {
      // no-op
    }
  };

  return (
    <div className={showPurchasePricingBar ? 'App App--purchasePricingBar' : 'App'}>
      <ScrollToTop />
      <AnalyticsTracker />
      <Navbar cartCount={cartItems.length} onCartClick={() => setShowCart(true)} />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/schools" element={<SchoolsPage />} />
          <Route path="/school-days" element={<SchoolDaysPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about-me" element={<AboutMePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faqs" element={<FAQPage />} />
          <Route path="/school/:schoolName" element={<SchoolDaysPage />} />
          <Route
            path="/school/:schoolName/day/:date"
            element={<DayPhotosPage onAddToCart={addToCart} />}
          />
          <Route path="/legal-notice" element={<LegalNoticePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-conditions" element={<TermsConditionsPage />} />
          <Route
            path="/checkout/success"
            element={<CheckoutSuccessPage onMount={clearPendingCart} />}
          />
          <Route
            path="/checkout/cancel"
            element={<CheckoutCancelPage onMount={restorePendingCart} />}
          />
          <Route path="/cuenta" element={<AccountGalleryPage />} />
          <Route path="/cuenta/entrar" element={<AccountLoginPage />} />
          <Route path="/cuenta/verificar-email" element={<VerifyEmailPage />} />
          <Route path="/cuenta/restablecer-contrasena" element={<AccountResetPasswordPage />} />
          <Route path="/cuenta/oauth-callback" element={<OAuthCallbackPage />} />
          <Route path="/fotografo/registro" element={<PhotographerRegisterPage />} />
        </Routes>
      </Suspense>

      <CartModal
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        items={displayItems}
        onRemoveItem={removeFromCart}
        totalPrice={getTotalPrice()}
        onCheckoutStart={handleCheckoutStart}
      />

      {showPurchasePricingBar ? <PurchasePricingBar /> : null}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <PricingProvider>
        <AuthProvider>
          <Router>
            <AppMain />
          </Router>
        </AuthProvider>
      </PricingProvider>
    </LanguageProvider>
  );
};

export default App;
